// src/lib/analytics-events.ts
//
// Esquema de eventos + funciones de captura para PostHog (init/Provider en
// src/lib/analytics.tsx). Separado para que react-refresh no rompa fast-refresh
// en analytics.tsx: ese archivo solo exporta el componente Provider, este solo
// exporta funciones/hooks/constantes. Expone track() TIPADO para que nadie
// invente nombres de eventos sueltos: solo los del esquema
// (social/09_measurement_schema.md) compilan.

import { createContext, useContext } from "react";
// PostHog es la instancia que devuelve el import(); PostHogInterface es lo que
// entrega el callback loaded() y lo que espera captureAttribution().
import type { PostHog, PostHogInterface } from "posthog-js";
import { captureAttribution } from "./attribution";
import { isPaymentLocation } from "./paymentPrivacy";

// ---- Esquema de eventos (la union es el contrato; añadir aquí = añadir al doc) ----
type TfdEvent =
  | "tfd_aplicar_click"
  | "tfd_intake_opened"
  | "tfd_account_created"
  | "tfd_gate_pass"
  | "tfd_gate_fail"
  | "tfd_deferred_capture"
  | "tfd_application_step_completed"
  | "tfd_application_submit"
  | "tfd_subscribe_click"
  | "tfd_subscribe"
  | "tfd_chat_opened"
  | "tfd_chat_message"
  | "tfd_global_view"
  | "tfd_global_cta_click";
// (tfd_interview_*, tfd_offer_made, tfd_payment son SERVER-SIDE, ver posthog_server_capture)

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  "https://eu.i.posthog.com";

let started = false;

export function isAnalyticsStarted() {
  return started;
}

export const CONSENT_STORAGE_KEY = "tfd_consent";

// ---- Carga diferida de posthog-js ------------------------------------------
//
// posthog-js son 225 kB del entry chunk de 931 kB, y initPosthog() corre dentro
// de un useEffect: o sea, DESPUES del primer render. Importarlo estaticamente lo
// ponia en la ruta critica del arranque sin ninguna razon. Ahora se carga con
// import() dinamico y el bundle deja de esperarlo para montar.
//
// El costo: entre initPosthog() y que el chunk aterrice hay una ventana (una
// descarga de chunk, ya post-mount) donde no existe todavia el objeto posthog.
// Las llamadas explicitas de ese lapso se encolan aca y se vacian al llegar.
//
// Lo que la cola NO puede rescatar: capture_exceptions y autocapture instalan
// los handlers globales del PROPIO posthog-js, que no existen hasta que el chunk
// carga. Una excepcion en esa ventana no se captura y no hay forma de encolarla,
// porque nadie la llama explicitamente. Es el precio consciente de sacar 225 kB
// del arranque.
let ph: PostHog | null = null;

type PosthogJob = (p: PostHog) => void;
const pending: PosthogJob[] = [];

// Tope por si el import falla y algo sigue llamando track() en un loop: sin esto
// el array crece sin limite. 50 eventos es mucho mas de lo que cabe en la ventana
// real de carga.
const MAX_PENDING = 50;

function withPosthog(job: PosthogJob) {
  // Mismo gate que antes: si analytics no arranco (sin KEY, o SSR), es no-op.
  if (!started || (typeof window !== "undefined" && isPaymentLocation(window.location))) return;
  if (ph) {
    job(ph);
    return;
  }
  if (pending.length >= MAX_PENDING) return;
  pending.push(job);
}

// Re-aplicar la eleccion guardada del banner en cada carga: el opt-out de
// posthog-js persiste solo, pero disable_session_recording:true significa que la
// grabacion no se re-activa sola tras un reload aunque el usuario ya haya
// aceptado antes. Opera sobre la instancia que entrega loaded() en vez de las
// funciones exportadas, para no depender de si `ph` ya quedo asignado.
function applyStoredConsent(p: PostHogInterface) {
  const consent = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  if (isPaymentLocation(window.location)) { p.stopSessionRecording(); return; }
  if (consent === "granted") {
    p.opt_in_capturing();
    p.startSessionRecording();
  }
  if (consent === "denied") p.opt_out_capturing();
}

export function initPosthog() {
  if (typeof window === "undefined") return;
  if (isPaymentLocation(window.location)) { ph?.stopSessionRecording(); pending.length = 0; return; }
  if (started || !KEY) return;
  // started se pone en true SINCRONICAMENTE, antes de que resuelva el import, para
  // que isAnalyticsStarted() siga significando exactamente lo mismo que antes
  // para quien lo consulte.
  started = true;

  void import("posthog-js")
    .then(({ default: posthog }) => {
      if (isPaymentLocation(window.location)) { started = false; pending.length = 0; return; }
      initWith(posthog);
      ph = posthog;
      // splice antes de iterar: un job que llame a track() no se pierde ni
      // reentra sobre el array que estamos recorriendo.
      for (const job of pending.splice(0, pending.length)) job(posthog);
    })
    .catch(() => {
      // El chunk no cargo. Volver a false deja isAnalyticsStarted() honesto y
      // convierte las llamadas siguientes en no-op en vez de acumular memoria.
      started = false;
      pending.length = 0;
    });
}

function initWith(posthog: PostHog) {
  posthog.init(KEY, {
    api_host: HOST,
    person_profiles: "identified_only", // no perfiles anonimos por cada visita/bot
    capture_pageview: false, // lo hacemos manual por el router del SPA
    autocapture: true,
    before_send: (event) => isPaymentLocation(window.location) ? null : event,
    session_recording: { blockSelector: ".ph-block" },
    // Unhandled exceptions + unhandled promise rejections, capturados por
    // posthog-js mismo. Sin esto, error boundaries y los capture() explicitos
    // son la UNICA fuente: se pierde todo lo que un error boundary no ve
    // (handlers de eventos, codigo async, cualquier cosa fuera del render tree).
    capture_exceptions: true,
    // Web vitals (FCP/LCP). Turned on 2026-08-12 because the boot watchdog in
    // vite.config.ts had to guess its grace period: `$web_vitals` had not been
    // seen in 30 days, so NOTHING measured how long a real Colombian phone
    // takes to mount and no honest p99 existed. This is the measurement that
    // makes that number derivable instead of invented.
    capture_performance: true,
    // Session replay arranca APAGADO por defecto: muchos aplicantes son
    // menores, y el replay es la superficie sensible (graba pantalla/mouse),
    // no los eventos. El banner de consentimiento (ConsentBanner.tsx) llama a
    // grantAnalyticsConsent(), que activa la grabación via
    // posthog.startSessionRecording() explícitamente. Los eventos no-PII
    // (tfd_*, $exception, $pageview) siguen capturando por defecto hasta que
    // el usuario elige Rechazar (revokeAnalyticsConsent -> opt_out_capturing).
    disable_session_recording: true,
    loaded: (p) => {
      p.register({
        product: "tfd",
        env: import.meta.env.MODE === "production" ? "prod" : "preview",
        cohorte: "cohorte-1",
      });
      captureAttribution(p); // primer/ultimo toque (attribution.ts)
      applyStoredConsent(p);
    },
  });
}

/**
 * track TIPADO. properties es libre pero el nombre esta acotado al esquema.
 *
 * El timestamp se toma AL LLAMAR, no al vaciar la cola, para que un evento
 * disparado antes de que cargue posthog-js no llegue fechado tarde.
 */
export function track(event: TfdEvent, properties?: Record<string, unknown>) {
  const timestamp = new Date();
  withPosthog((p) => p.capture(event, properties, { timestamp }));
}

/**
 * $pageview manual (posthog-js no ve los cambios de ruta de un SPA).
 * Vive aca y no en analytics.tsx para que ese archivo no tenga que importar
 * posthog-js y volver a anclarlo al entry chunk.
 */
export function capturePageview(url: string) {
  const timestamp = new Date();
  withPosthog((p) => p.capture("$pageview", { $current_url: url }, { timestamp }));
}

/**
 * identificar al usuario tras login/signup.
 *
 * Sin timestamp explicito: identify() no acepta CaptureOptions, asi que si cae en
 * la cola queda fechado al vaciarse. Es una propiedad de persona, no un evento en
 * una linea de tiempo, de modo que el desfase no distorsiona nada.
 */
export function identify(distinctId: string, props?: Record<string, unknown>) {
  withPosthog((p) => p.identify(distinctId, props));
}

/**
 * Capturar una excepcion real via la API dedicada de PostHog (no un evento
 * custom): parsea el stack trace correctamente y la agrupa en Error Tracking.
 * Usar esto en vez de track("tfd_client_error", ...) en cualquier catch-site
 * nuevo, para que todos los errores del cliente compartan una sola forma.
 */
export function captureException(error: unknown, properties?: Record<string, unknown>) {
  // Sin timestamp explicito a proposito: captureException() no acepta un objeto de
  // opciones. Reenviarlo como capture("$exception", ...) para ganar el timestamp
  // perderia el parseo de stack trace, que es justamente la razon de existir de
  // esta funcion segun el comentario de arriba.
  withPosthog((p) => p.captureException(error, properties));
}

/** llamar cuando el consentimiento (banner de cookies) queda aceptado. */
export function grantAnalyticsConsent() {
  withPosthog((p) => {
    p.opt_in_capturing();
    p.startSessionRecording();
  });
}

/** llamar cuando el consentimiento queda rechazado: respeta el "no" por completo. */
export function revokeAnalyticsConsent() {
  withPosthog((p) => p.opt_out_capturing());
}

/** Solo para tests: vacia el estado del modulo entre casos. */
export function __resetAnalyticsForTests() {
  started = false;
  ph = null;
  pending.length = 0;
}

export const AnalyticsContext = createContext<null>(null);

export const useAnalytics = () => useContext(AnalyticsContext);
